import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, Building2, Loader2 } from 'lucide-react';

import { getOrganisation } from '../services/intakeApi';
import { applyBranding } from '../lib/branding';
import PoweredBy from '../components/PoweredBy';
import NotFoundPage from './NotFoundPage';
import { Button } from '../components/ui/button';

/**
 * What a customer sees at `/:teamId` — the shop's own front door.
 *
 * This used to be a dead end: the bare organisation URL matched nothing and
 * showed the not-found page, which is a poor result for the most guessable,
 * most likely-to-be-shared link a shop has.
 */
const OrganisationPage = () => {
  const { teamId } = useParams();
  const [state, setState] = useState({ loading: true, organisation: null, error: null });

  useEffect(() => {
    let cancelled = false;

    getOrganisation(teamId).then((result) => {
      if (cancelled) return;
      setState({
        loading: false,
        organisation: result.organisation || null,
        error: result.valid ? null : result.error
      });
    });

    return () => { cancelled = true; };
  }, [teamId]);

  useEffect(() => {
    if (!state.organisation?.branding) return undefined;
    return applyBranding(document.documentElement, state.organisation.branding);
  }, [state.organisation]);

  useEffect(() => {
    if (state.organisation?.name) document.title = state.organisation.name;
  }, [state.organisation]);

  if (state.loading) {
    return (
      <div className="min-h-dvh flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // A shop that isn't taking online requests still gets a page with its name on
  // it, rather than an error implying the link was wrong.
  if (state.error && !state.organisation) return <NotFoundPage error={state.error} />;

  const { organisation } = state;
  const closed = Boolean(state.error);

  return (
    <div className="min-h-dvh bg-background flex flex-col">
      <main className="flex flex-1 items-center justify-center px-4 py-12">
        <div className="w-full max-w-md text-center">
          {organisation?.branding?.logoUrl ? (
            <img
              src={organisation.branding.logoUrl}
              alt={organisation.name}
              className="mx-auto mb-6 h-16 w-auto max-w-56 object-contain"
            />
          ) : (
            <span className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-[var(--brand-accent)] text-[var(--brand-accent-foreground)]">
              <Building2 className="h-6 w-6" />
            </span>
          )}

          <h1 className="text-2xl font-semibold tracking-tight">{organisation?.name}</h1>

          {closed ? (
            <p className="mt-3 text-sm text-muted-foreground">{state.error}</p>
          ) : (
            <>
              <p className="mt-3 text-sm text-muted-foreground">
                Send us the details and photos of the damage, and we'll come back to you with a quote.
              </p>

              <Button
                asChild
                size="lg"
                className="mt-8 w-full bg-[var(--brand-accent)] text-[var(--brand-accent-foreground)] hover:opacity-90"
              >
                <Link to={`/${teamId}/form/1`}>
                  Request a quote
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            </>
          )}
        </div>
      </main>

      <footer className="px-4 pb-safe pt-2">
        <PoweredBy />
      </footer>
    </div>
  );
};

export default OrganisationPage;
